import {
    createStyles,
    FormHelperText,
    InputLabel,
    StyledComponentProps,
    WithStyles,
    withStyles,
} from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import { Chip, IChip, RelodeTheme } from "../";
import { CheckIcon } from "../../assets/icons";

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {
            backgroundColor: "transparent",
            marginBottom: 16,
            position: "relative",
            display: "flex",
            flexDirection: "column",
        },
        description: {
            top: 0,
            left: 0,
            position: "absolute",
        },
        tagList: {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            marginTop: 24,
            "& div:last-child": {
                marginRight: 0,
            },
            "& div": {
                marginRight: 8,
                marginBottom: 8,
            },
        },
        matched: {
            backgroundColor: theme.relode.colors.chip.matched.light,
            color: theme.relode.colors.chip.matched.contrastText,
            "&:focus": {
                backgroundColor: theme.relode.colors.chip.matched.light,
            },
            "&:hover": {
                backgroundColor: theme.relode.colors.chip.matched.main,
            },
        },
        unMatched: {
            backgroundColor: theme.relode.colors.chip.unselected.light,
            color: theme.relode.colors.chip.unselected.contrastText,
            "&:focus": {
                backgroundColor: theme.relode.colors.chip.unselected.light,
            },
            "&:hover": {
                backgroundColor: theme.relode.colors.chip.unselected.main,
            },
        },
        unMatchable: {
            backgroundColor: theme.relode.colors.chip.notMatchable.main,
            color: theme.relode.colors.chip.notMatchable.contrastText,
            "&:focus": {
                backgroundColor: theme.relode.colors.chip.notMatchable.main,
            },
            "&:hover": {
                backgroundColor: theme.relode.colors.chip.notMatchable.main,
            },
        },
        subText: {
            marginTop: theme.spacing(1),
        },
        error: {
            color: theme.palette.error.main,
        },
        helperText: {
            marginBottom: theme.spacing(3),
        },
        matchedIcon: {
            color: theme.palette.secondary.main,
        },
        checkIcon: {
            width: "16px",
            height: "16px",
        },
    });

interface OwnProps {
    baseId: string;
    style?: React.CSSProperties;
    description?: string;
    subText?: string;
    chips: IChip[] | IChip;
    isExclusive?: boolean; // there can be only one selected

    onChipClick?: (id: number, value?: any) => void;
    onChipDelete?: (id: number, tagTypeId?: number) => void;

    error?: boolean;
    helperText?: string;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

export interface ChipMatchCollectionProps
    extends OwnProps,
        StyledComponentProps<any> {}

export const ChipMatchCollection = withStyles(styles, {
    name: "ChipMatchCollection",
})(
    class extends React.Component<Props> {
        handleChipClick = (id: number, value?: any) => {
            const { chips, isExclusive, onChipClick } = this.props;

            if (chips instanceof Array && isExclusive === true) {
                chips.forEach((chip, i) => {
                    if (chip.id !== id) {
                        chip.isMatched = false;
                        chip.canEdit = true;
                    }
                });
            }

            if (onChipClick) onChipClick(id, value);
        };

        renderTags = () => {
            const {
                chips,
                onChipClick,
                classes,
                style,
                onChipDelete,
                ...passthrough
            } = this.props;

            const chipsArray = Array.isArray(chips) ? chips : [chips];
            const dataCy = passthrough["data-cy"];

            return (
                <div data-cy={dataCy}>
                    {chipsArray.map(chip => (
                        <Chip
                            style={style}
                            key={`${chip.id}-${chip.name}`}
                            value={chip}
                            label={chip.name}
                            id={chip.id}
                            icon={
                                chip.isMatched ? (
                                    <CheckIcon
                                        className={classes.matchedIcon}
                                        classes={{ root: classes.checkIcon }}
                                    />
                                ) : undefined
                            }
                            selected={chip.isMatched}
                            enabled={chip.canEdit}
                            variant={chip.canMatch ? "default" : "outlined"}
                            className={
                                chip.canMatch
                                    ? chip.isMatched
                                        ? classes.matched
                                        : classes.unMatched
                                    : classes.unMatchable
                            }
                            onChipClick={
                                onChipClick && chip.canEdit && chip.canMatch
                                    ? this.handleChipClick
                                    : undefined
                            }
                            onChipDelete={
                                onChipDelete && chip.canEdit && chip.canMatch
                                    ? onChipDelete
                                    : undefined
                            }
                        />
                    ))}
                </div>
            );
        };

        hasTags = () => {
            const { chips } = this.props;
            if (!chips) return false;

            if (Array.isArray(chips)) return chips.length > 0;

            return true;
        };

        render() {
            const {
                baseId,
                classes,
                description,
                subText,
                error,
                helperText,
            } = this.props;
            const labelClasses = classnames(classes.description, {
                [classes.error]: error,
            });
            const helperTextClasses = classnames(classes.helperText, {
                [classes.error]: error,
            });
            const extendedId = `${baseId}ChipMatchCollection`;

            return this.hasTags() ? (
                <div className={classes.root} id={`${extendedId}Wrapper`}>
                    {description && (
                        <InputLabel className={labelClasses} shrink>
                            {description}
                        </InputLabel>
                    )}
                    <div
                        className={classes.tagList}
                        id={`${extendedId}TagList`}
                    >
                        {this.renderTags()}
                    </div>
                    {subText && (
                        <InputLabel className={classes.subText} shrink>
                            {subText}
                        </InputLabel>
                    )}
                    {error && (
                        <FormHelperText className={helperTextClasses}>
                            {helperText}
                        </FormHelperText>
                    )}
                </div>
            ) : null;
        }
    }
);
